import { dedupedGet } from 'shared/services/api/dedupedGet';
import { identifyPosthogUser } from 'shared/utils/posthogClient';

// The team context calls this every time its selected-team object changes, and
// on a page load that object is rebuilt several times over (cache, fetch,
// subscription) for the same person in the same team — each one an identify
// and two group calls to the analytics API, several of each per load. Once per
// person and team per page is all the information there is.
let lastSynced = null;

/** Forget who was identified, so the next sync sends again. Call on logout. */
export function forgetSyncedIdentity() {
  lastSynced = null;
}

/**
 * Identify the signed-in user to PostHog (once per person and team per page).
 * Trodo's own browser SDK is no longer run on the app, so there is no Trodo
 * identify or group call any more; the name is kept for its callers.
 */
export async function syncTrodoIdentifyAndTeam(user, team) {
  if (typeof window === 'undefined') return;
  const email = user?.email;
  if (!email || typeof email !== 'string') return;
  const syncKey = JSON.stringify([email, String(team?.id ?? team?._id ?? ''), team?.name ?? '']);
  if (syncKey === lastSynced) return;
  lastSynced = syncKey;
  identifyPosthogUser(user, team);
}

/**
 * Fetches billing/subscription for the default team and updates localStorage + dispatches teamDataUpdated.
 * Runs in parallel with preloadData during login/signup to shorten time on the loading overlay.
 */
export async function fetchAndApplySubscriptionForDefaultTeam(defaultTeam, logContext = 'auth') {
  if (!defaultTeam?._id) return;
  try {
    // Deduped: teamService and stripeService each fetch this same status on
    // load too, and none of the three knows about the others. Three identical
    // concurrent GETs is three of the browser's six connections to this origin,
    // which is why slower siblings on the same page sat queued behind them.
    const subscriptionResponse = await dedupedGet(`/billing/status/${defaultTeam._id}`);
    if (subscriptionResponse.data?.success && subscriptionResponse.data.data) {
      const subscriptionData = subscriptionResponse.data.data;
      const { plan, status, isActive, limits, accessiblePages } = subscriptionData;
      const updatedTeamData = {
        ...defaultTeam,
        subscription: {
          plan: plan || 'free',
          status: status || 'incomplete',
        },
      };
      localStorage.setItem('selectedTeamData', JSON.stringify(updatedTeamData));
      const subscriptionCache = {
        isActive: isActive || false,
        plan: plan || 'free',
        status: status || 'incomplete',
        limits: limits || {},
        accessiblePages: accessiblePages || ['dashboard', 'settings'],
        timestamp: Date.now(),
      };
      sessionStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
      localStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
      window.dispatchEvent(
        new CustomEvent('teamDataUpdated', { detail: { teamData: updatedTeamData } })
      );
    }
  } catch (e) {
    console.error(`Error fetching subscription data during ${logContext}:`, e);
  }
}
